import { useMemo } from "react";
import { useAccountBalances } from "@/hooks/firestore/user/useAccountBalances";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { withdrawalSettings } from "@/hooks/firestore/models/withdrawalSettings";
import { convertToNumber } from "utilities/helpers";
import type { Currency } from "../models/currency";

const withdrawalSettingsModel = withdrawalSettings.array();

const useWithdrawalSettings = (currency: string) => {
  const { accounts, isLoading: accountsLoading } = useAccountBalances();

  const {
    data: settings,
    isLoading: settingsLoading,
    error,
  } = useFirestore<typeof withdrawalSettingsModel, "currency", Currency>(
    `/users/{userId}/withdrawalSettings`,
    withdrawalSettingsModel,
    {
      keyPath: "currency",
    },
  );

  const combinedSettings = useMemo(() => {
    if (accountsLoading || settingsLoading || !accounts) {
      return {
        amountToTurnover: 0,
        availableBalance: 0,
        withdrawableAmount: 0,
      };
    }

    const account = accounts[currency.toLowerCase()];
    const availableBalance = account?.available.balanceInCents || 0;
    const amountToTurnover =
      (settings || []).find((entry) => entry.currency === currency)
        ?.amountToTurnover || 0;

    return {
      amountToTurnover,
      availableBalance,
      withdrawableAmount: Math.max(
        0,
        convertToNumber(availableBalance) - convertToNumber(amountToTurnover),
      ),
    };
  }, [accountsLoading, settingsLoading, settings, accounts]);

  return {
    settings: combinedSettings,
    isLoading: settingsLoading || accountsLoading,
    error,
  };
};

type WithdrawalSettings = Exclude<
  ReturnType<typeof useWithdrawalSettings>["settings"],
  undefined
>;

export { useWithdrawalSettings };
export type { WithdrawalSettings };
